/** 한국(Asia/Seoul) 달력일 기준 — 결제·만료일 안내와 동일하게 맞춤 */
export const BILLING_NUDGE_TZ = 'Asia/Seoul'

export const BILLING_PAYWALL_MODAL_LAST_DATE_KEY = 'sgez.billingPaywall.modalLastDateKst'
export const STORE_SUBSCRIPTION_OVERLAP_MODAL_LAST_DATE_KEY = 'sgez.storeSubscriptionOverlap.modalLastDateKst'
export const TRIAL_SUBSCRIPTION_NOTICE_DISMISSED_KEY_PREFIX = 'sgez.trialSubscription.noticeDismissed'

export function dateStringInTimeZone(date, timeZone) {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return null
  return new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(date)
}

/** @param {string|null|undefined} isoInstant */
export function dateStringForInstantInTimeZone(isoInstant, timeZone) {
  if (!isoInstant) return null
  const d = new Date(isoInstant)
  if (Number.isNaN(d.getTime())) return null
  return dateStringInTimeZone(d, timeZone)
}

/**
 * 달력일 기준: endYmd - todayYmd (정수 일 수).
 * @param {string} todayYmd `YYYY-MM-DD`
 * @param {string} endYmd `YYYY-MM-DD`
 */
export function calendarDaysFromTodayToEndInclusive(todayYmd, endYmd) {
  if (!todayYmd || !endYmd) return null
  const t = new Date(`${todayYmd}T12:00:00`)
  const e = new Date(`${endYmd}T12:00:00`)
  if (Number.isNaN(t.getTime()) || Number.isNaN(e.getTime())) return null
  return Math.round((e.getTime() - t.getTime()) / 86400000)
}

/**
 * 조직이 구독료를 부담하는 경우 개인 결제 유도 배너/모달 제외
 * @param {Record<string, unknown>|null|undefined} orgContract `/billing/me` 의 orgContract
 */
export function isOrgPaysCurrentlyEffective(orgContract) {
  if (!orgContract || orgContract.currentlyEffective !== true) return false
  const bm = String(orgContract.billingMode || orgContract.billingModel || '').toUpperCase()
  return bm === 'ORG_PAYS'
}

/**
 * @typedef {'none' | 'warn7' | 'warn4' | 'warn1' | 'lastDay' | 'expired' | 'suspended'} BillingPaywallNudgeLevel
 * @typedef {{ level: BillingPaywallNudgeLevel, daysLeft: number|null, endsAtYmd: string|null }} BillingPaywallNudge
 */

/**
 * @param {{ subscription?: Record<string, unknown>|null, orgContract?: Record<string, unknown>|null }} billingMe
 * @returns {BillingPaywallNudge}
 */
export function computeBillingPaywallNudge(billingMe) {
  if (!billingMe || typeof billingMe !== 'object') {
    return { level: 'none', daysLeft: null, endsAtYmd: null }
  }
  if (isOrgPaysCurrentlyEffective(billingMe.orgContract)) {
    return { level: 'none', daysLeft: null, endsAtYmd: null }
  }
  const subscription = billingMe.subscription && typeof billingMe.subscription === 'object'
    ? billingMe.subscription
    : null
  if (!subscription) {
    return { level: 'none', daysLeft: null, endsAtYmd: null }
  }
  const status = String(subscription.status || '').toUpperCase()
  if (status === 'TRIAL') {
    return { level: 'none', daysLeft: null, endsAtYmd: null }
  }
  if (status === 'SUSPENDED') {
    return { level: 'suspended', daysLeft: null, endsAtYmd: dateStringForInstantInTimeZone(/** @type {string} */ (subscription.endsAt), BILLING_NUDGE_TZ) }
  }
  if (status === 'EXPIRED') {
    return { level: 'expired', daysLeft: null, endsAtYmd: dateStringForInstantInTimeZone(/** @type {string} */ (subscription.endsAt), BILLING_NUDGE_TZ) }
  }

  const endsAt = subscription.endsAt ? String(subscription.endsAt) : null
  const endsAtYmd = dateStringForInstantInTimeZone(endsAt, BILLING_NUDGE_TZ)
  const todayYmd = dateStringInTimeZone(new Date(), BILLING_NUDGE_TZ)
  if (!endsAtYmd || !todayYmd) {
    return { level: 'none', daysLeft: null, endsAtYmd }
  }
  const daysLeft = calendarDaysFromTodayToEndInclusive(todayYmd, endsAtYmd)
  if (daysLeft === null) {
    return { level: 'none', daysLeft: null, endsAtYmd }
  }
  if (daysLeft < 0) {
    return { level: 'expired', daysLeft, endsAtYmd }
  }
  if (daysLeft === 0) {
    return { level: 'lastDay', daysLeft: 0, endsAtYmd }
  }
  if (daysLeft === 1) return { level: 'warn1', daysLeft: 1, endsAtYmd }
  if (daysLeft === 4) return { level: 'warn4', daysLeft: 4, endsAtYmd }
  if (daysLeft === 7) return { level: 'warn7', daysLeft: 7, endsAtYmd }
  return { level: 'none', daysLeft, endsAtYmd }
}

/** @param {BillingPaywallNudgeLevel} level */
export function billingPaywallNudgeNeedsUi(level) {
  return level !== 'none'
}

function emptyTrialSubscriptionNotice() {
  return {
    visible: false,
    level: 'none',
    daysLeft: null,
    trialDays: null,
    startedAtYmd: null,
    endsAtYmd: null,
    subscriptionId: null,
    todayYmd: null,
  }
}

function firstNonEmptyText(...values) {
  for (const value of values) {
    const text = value == null ? '' : String(value).trim()
    if (text) return text
  }
  return null
}

/**
 * @typedef {'none' | 'start' | 'warn3' | 'warn1' | 'lastDay'} TrialSubscriptionNoticeLevel
 * @typedef {{
 *   visible: boolean,
 *   level: TrialSubscriptionNoticeLevel,
 *   daysLeft: number|null,
 *   trialDays: number|null,
 *   startedAtYmd: string|null,
 *   endsAtYmd: string|null,
 *   subscriptionId: string|null,
 *   todayYmd: string|null,
 * }} TrialSubscriptionNotice
 */

/**
 * @param {{ subscription?: Record<string, unknown>|null, plan?: Record<string, unknown>|null, orgContract?: Record<string, unknown>|null }} billingMe
 * @param {Date} [now]
 * @returns {TrialSubscriptionNotice}
 */
export function computeTrialSubscriptionNotice(billingMe, now = new Date()) {
  if (!billingMe || typeof billingMe !== 'object') return emptyTrialSubscriptionNotice()
  if (isOrgPaysCurrentlyEffective(billingMe.orgContract)) return emptyTrialSubscriptionNotice()

  const subscription = billingMe.subscription && typeof billingMe.subscription === 'object'
    ? billingMe.subscription
    : null
  if (!subscription) return emptyTrialSubscriptionNotice()

  const activeTrialGrant = subscription.activeTrialGrant && typeof subscription.activeTrialGrant === 'object'
    ? subscription.activeTrialGrant
    : null
  const status = String(subscription.status || '').toUpperCase()
  const billingMode = String(subscription.billingMode || '').toUpperCase()
  const planCode = String(subscription.planCode || billingMe.plan?.planCode || '').toUpperCase()
  const isTrial = status === 'TRIAL' || billingMode === 'TRIAL' || planCode === 'TRIAL'
  if (!isTrial) return emptyTrialSubscriptionNotice()

  const endsAt = firstNonEmptyText(subscription.endsAt, subscription.endAt, activeTrialGrant?.endAt)
  const endsAtYmd = dateStringForInstantInTimeZone(endsAt, BILLING_NUDGE_TZ)
  const todayYmd = dateStringInTimeZone(now, BILLING_NUDGE_TZ)
  if (!endsAtYmd || !todayYmd) return emptyTrialSubscriptionNotice()

  const daysLeft = calendarDaysFromTodayToEndInclusive(todayYmd, endsAtYmd)
  if (daysLeft === null || daysLeft < 0) return emptyTrialSubscriptionNotice()

  const startedAt = firstNonEmptyText(subscription.startedAt, subscription.startAt, activeTrialGrant?.startAt)
  const startedAtYmd = dateStringForInstantInTimeZone(startedAt, BILLING_NUDGE_TZ)
  const computedTrialDays = startedAtYmd
    ? calendarDaysFromTodayToEndInclusive(startedAtYmd, endsAtYmd)
    : null
  const configuredTrialDays = Number(billingMe.plan?.defaultTrialDays)
  const trialDays = computedTrialDays !== null && computedTrialDays > 0
    ? computedTrialDays
    : (Number.isFinite(configuredTrialDays) && configuredTrialDays > 0 ? configuredTrialDays : 7)

  let level = 'none'
  if (daysLeft > 3) level = 'start'
  else if (daysLeft === 3) level = 'warn3'
  else if (daysLeft === 1) level = 'warn1'
  else if (daysLeft === 0) level = 'lastDay'

  return {
    visible: level !== 'none',
    level,
    daysLeft,
    trialDays,
    startedAtYmd,
    endsAtYmd,
    subscriptionId: subscription.id == null ? null : String(subscription.id),
    todayYmd,
  }
}

/**
 * @param {Record<string, unknown>|null|undefined} billingMe
 */
export function computeStoreSubscriptionOverlapNotice(billingMe) {
  if (!billingMe || typeof billingMe !== 'object') {
    return { visible: false, remainingCreditDays: 0, maxCreditDays: 90, creditCapped: false, subscriptionEndsAtYmd: null }
  }
  const overlap = billingMe.storeSubscriptionOverlap && typeof billingMe.storeSubscriptionOverlap === 'object'
    ? billingMe.storeSubscriptionOverlap
    : null
  const visible = overlap?.visible === true
  const remainingCreditDays = Number(overlap?.remainingCreditDays ?? 0)
  const maxCreditDays = Number(overlap?.maxCreditDays ?? 90)
  const creditCapped = overlap?.creditCapped === true || remainingCreditDays >= maxCreditDays
  const subscriptionEndsAt = overlap?.subscriptionEndsAt ? String(overlap.subscriptionEndsAt) : null
  return {
    visible,
    remainingCreditDays: Number.isFinite(remainingCreditDays) ? Math.max(0, remainingCreditDays) : 0,
    maxCreditDays: Number.isFinite(maxCreditDays) && maxCreditDays > 0 ? maxCreditDays : 90,
    creditCapped,
    subscriptionEndsAtYmd: dateStringForInstantInTimeZone(subscriptionEndsAt, BILLING_NUDGE_TZ),
  }
}
